import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Link, Stack, Tab, Tabs, Typography } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { Trans, useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { HUBSPOT_PORTAL_ID } from 'src/appConfig';
import { isProblemDetailCode, isProblemDetailTextOr } from 'src/common/ProblemDetail';
import { NotificationSeverity, showSnackbar } from 'src/features/notifications/notifications.slice';
import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';

import { ProductRequirementGroup, RequirementFieldOption } from '../data-collection.dto';
import {
    DealWithServices,
    RealEstateAsset,
    ServiceStatus,
    ServiceWithStatus,
} from '../real-estate-asset-management.dto';
import {
    createServiceAssetMapping,
    deleteServiceAssetMapping,
    fetchRealEstateAssetServices,
    fetchUnassignedServices,
} from '../real-estate-asset-management.slice';
import { ServiceInformationTab } from './ServiceInformationTab';
import {
    activeProductIds,
    enabledServiceInfoGroupCodes,
    serviceInformationGroups,
} from './service-information.helpers';
import { ServiceSelector } from './ServiceSelector';
import { DealGroupForSelector, serviceLabel, serviceTypeKey } from './services-tab.helpers';

const hubspotLineItemUrl = (lineItemId: string) =>
    `https://app-eu1.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/record/0-162/${lineItemId}`;

function partitionServicesByStatus(deals: DealWithServices[]): Record<ServiceStatus, DealWithServices[]> {
    const out: Record<ServiceStatus, DealWithServices[]> = { ACTIVE: [], DELIVERED: [], CANCELLED: [] };
    for (const deal of deals) {
        for (const status of ['ACTIVE', 'DELIVERED', 'CANCELLED'] as ServiceStatus[]) {
            const services = deal.services.filter((s) => s.status === status);
            if (services.length > 0) {
                out[status].push({
                    ...deal,
                    services: services.sort((a, b) => a.name.localeCompare(b.name, 'de')),
                });
            }
        }
    }
    for (const status of ['ACTIVE', 'DELIVERED', 'CANCELLED'] as ServiceStatus[]) {
        out[status].sort((a, b) => (b.closingDate ?? '').localeCompare(a.closingDate ?? ''));
    }
    return out;
}

/**
 * Flatten and merge already-mapped + unassigned services for a single status,
 * then re-group by deal. Already-mapped services are flagged so the selector
 * could distinguish them visually (currently unused — both are toggleable).
 */
function buildSelectorGroups(
    reaServices: DealWithServices[],
    unassignedServices: DealWithServices[],
    statuses: ServiceStatus[],
): DealGroupForSelector[] {
    const groupsById = new Map<string, DealGroupForSelector>();
    const upsert = (deal: DealWithServices, service: ServiceWithStatus, alreadyAssigned: boolean) => {
        let group = groupsById.get(deal.id);
        if (!group) {
            group = {
                dealId: deal.id,
                dealName: deal.name,
                dealExternalId: deal.externalId,
                dealClosingDate: deal.closingDate,
                services: [],
            };
            groupsById.set(deal.id, group);
        }
        group.services.push({
            ...service,
            dealId: deal.id,
            dealName: deal.name,
            dealExternalId: deal.externalId,
            dealClosingDate: deal.closingDate,
            alreadyAssigned,
        });
    };

    for (const deal of reaServices) {
        for (const s of deal.services) if (statuses.includes(s.status)) upsert(deal, s, true);
    }
    for (const deal of unassignedServices) {
        for (const s of deal.services) if (statuses.includes(s.status)) upsert(deal, s, false);
    }
    return [...groupsById.values()]
        .sort((a, b) => {
            const aAssigned = a.services.some((s) => s.alreadyAssigned) ? 1 : 0;
            const bAssigned = b.services.some((s) => s.alreadyAssigned) ? 1 : 0;
            if (bAssigned !== aAssigned) return bAssigned - aAssigned;
            return (b.dealClosingDate ?? '').localeCompare(a.dealClosingDate ?? '');
        })
        .map((group) => ({
            ...group,
            services: [...group.services].sort((a, b) => a.name.localeCompare(b.name, 'de')),
        }));
}

// ─── Change-summary helpers ──────────────────────────────────────────────────

type ServiceChange = {
    serviceId: string;
    label: string;
    op: 'add' | 'remove';
};

type DealChangeSummary = {
    dealId: string;
    dealExternalId: string;
    dealName: string;
    dealClosingDate: string | null;
    changes: ServiceChange[];
};

type ServiceMeta = {
    label: string;
    /** One-per-type key (`serviceKey ?? sku`), null when unknown. */
    typeKey: string | null;
    dealId: string;
    dealExternalId: string;
    dealName: string;
    dealClosingDate: string | null;
};

/** Build the flat service→deal-context lookup from a set of groups. */
function buildServiceMetaMap(groups: DealGroupForSelector[]): Map<string, ServiceMeta> {
    const map = new Map<string, ServiceMeta>();
    for (const group of groups) {
        for (const service of group.services) {
            map.set(service.id, {
                label: serviceLabel(service),
                typeKey: serviceTypeKey(service),
                dealId: group.dealId,
                dealExternalId: group.dealExternalId,
                dealName: group.dealName,
                dealClosingDate: group.dealClosingDate,
            });
        }
    }
    return map;
}

/**
 * Compute which services were added or removed relative to `initialIds`,
 * grouped by deal. Accepts a pre-built metadata map so the caller can
 * memoize it separately (it only changes when selectorGroups changes).
 */
function buildChangeSummary(
    editIds: string[],
    initialIds: Set<string>,
    metaById: Map<string, ServiceMeta>,
): DealChangeSummary[] {
    const editSet = new Set(editIds);

    const byDeal = new Map<string, DealChangeSummary>();
    const record = (id: string, op: 'add' | 'remove') => {
        const meta = metaById.get(id);
        if (!meta) return; // not visible in dropdown (e.g. CANCELLED status)
        let deal = byDeal.get(meta.dealId);
        if (!deal) {
            deal = {
                dealId: meta.dealId,
                dealExternalId: meta.dealExternalId,
                dealName: meta.dealName,
                dealClosingDate: meta.dealClosingDate,
                changes: [],
            };
            byDeal.set(meta.dealId, deal);
        }
        deal.changes.push({ serviceId: id, label: meta.label, op });
    };

    // Removals: was in the initial snapshot, no longer selected
    for (const id of initialIds) {
        if (!editSet.has(id)) record(id, 'remove');
    }
    // Additions: now selected, was not in the initial snapshot
    for (const id of editIds) {
        if (!initialIds.has(id)) record(id, 'add');
    }

    return [...byDeal.values()];
}

function UnsavedChangeSummary({ summary }: { summary: DealChangeSummary[] }) {
    const { t } = useTranslation();
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {t('Unsaved service changes')}:
            </Typography>
            <Stack spacing={1}>
                {summary.map((deal) => {
                    const datePart = deal.dealClosingDate
                        ? ` · ${dayjs(deal.dealClosingDate).format('YYYY-MM-DD, HH:mm')}`
                        : '';
                    return (
                        <Box key={deal.dealId}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                {t('Deal')}: {deal.dealExternalId} {deal.dealName}
                                {datePart}
                            </Typography>
                            <Stack spacing={0.25} sx={{ mt: 0.5, ml: 1 }}>
                                {deal.changes.map((change) => (
                                    <Typography key={change.serviceId} variant="body2">
                                        {/* Prefix is bold; textDecoration: none makes it explicitly un-struck */}
                                        <Typography
                                            component="span"
                                            variant="inherit"
                                            sx={{ fontWeight: 'bold', textDecoration: 'none' }}
                                        >
                                            {change.op === 'add' ? t('Added') : t('Removed')}:
                                        </Typography>{' '}
                                        {/* Only the service name gets struck through for removals */}
                                        <Typography
                                            component="span"
                                            variant="inherit"
                                            sx={change.op === 'remove' ? { textDecoration: 'line-through' } : undefined}
                                        >
                                            {change.label}
                                        </Typography>
                                    </Typography>
                                ))}
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
}

// ─── View components ──────────────────────────────────────────────────────────

function DealHeader({ deal }: { deal: DealWithServices }) {
    const { t } = useTranslation();
    const datePart = deal.closingDate ? ` · ${dayjs(deal.closingDate).format('YYYY-MM-DD, HH:mm')}` : '';
    return (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            {t('Deal')}: {deal.externalId} {deal.name}
            {datePart}
        </Typography>
    );
}

function ServicesViewSection({
    title,
    emptyText,
    deals,
}: {
    title: string;
    emptyText: string;
    deals: DealWithServices[];
}) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {title}
            </Typography>
            {deals.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {emptyText}
                </Typography>
            ) : (
                <Stack spacing={1.5}>
                    {deals.map((deal) => (
                        <Box key={deal.id}>
                            <DealHeader deal={deal} />
                            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                {deal.services.map((service) => (
                                    <Box
                                        key={service.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                            {serviceLabel(service)}
                                        </Typography>
                                        <Button
                                            component={Link}
                                            href={hubspotLineItemUrl(service.externalId)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            size="small"
                                            variant="outlined"
                                            endIcon={<OpenInNew fontSize="small" />}
                                            sx={{ flexShrink: 0 }}
                                        >
                                            <Trans i18nKey="Open in HubSpot" />
                                        </Button>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            )}
        </Box>
    );
}

function ConnectionsSubTab({
    realEstateAsset,
    onEditingChange,
}: {
    realEstateAsset: RealEstateAsset;
    onEditingChange?: (editing: boolean) => void;
}) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const {
        reaServices,
        reaServicesLoadedForId,
        isLoadingReaServices,
        unassignedServices,
        isLoadingUnassignedServices,
    } = useAppSelector((state) => state.realEstateAssetManagement);

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editServiceIds, setEditServiceIds] = useState<string[]>([]);
    // Snapshot of assigned IDs at the moment editing began — used to detect real changes
    // and to compute the unsaved-changes diff. State (not ref) so changeSummary re-renders
    // automatically when the snapshot is updated (e.g. after a partial-failure save).
    const [initialEditIds, setInitialEditIds] = useState<Set<string>>(new Set());

    const groupedServices = useMemo(() => partitionServicesByStatus(reaServices), [reaServices]);

    const selectorGroups = useMemo(
        () => buildSelectorGroups(reaServices, unassignedServices, ['ACTIVE', 'DELIVERED']),
        [reaServices, unassignedServices],
    );

    const currentlyAssignedServiceIds = useMemo(
        () => new Set(reaServices.flatMap((d) => d.services.map((s) => s.id))),
        [reaServices],
    );

    // Service metadata map — only rebuilt when selectorGroups changes, not on every
    // selection toggle, so buildChangeSummary iteration stays O(Δselection) per render.
    const serviceMetaById = useMemo(() => buildServiceMetaMap(selectorGroups), [selectorGroups]);

    // Diff between initial assignment snapshot and current selection, grouped by deal.
    const changeSummary = useMemo(
        () => buildChangeSummary(editServiceIds, initialEditIds, serviceMetaById),
        [editServiceIds, initialEditIds, serviceMetaById],
    );

    // Fetch data when the component mounts or the asset changes.
    // Also resets editing state and notifies the parent so stale selections from a
    // previous asset can't be saved and the parent's navigation guard is cleared.
    useEffect(() => {
        setIsEditing(false);
        setIsSaving(false);
        onEditingChange?.(false);
        dispatch(fetchUnassignedServices());
        dispatch(fetchRealEstateAssetServices(realEstateAsset.id));
    }, [dispatch, realEstateAsset.id, onEditingChange]);

    // Sync selection state whenever reaServices changes (initial load and after save).
    // reaServices is in deps so the effect fires when the post-save fetch response arrives,
    // even if reaServicesLoadedForId didn't change. isEditing guards against overwriting the
    // user's in-progress selection while they are actively editing.
    useEffect(() => {
        if (reaServicesLoadedForId === realEstateAsset.id && !isEditing) {
            const ids = [...currentlyAssignedServiceIds];
            setInitialEditIds(new Set(ids));
            setEditServiceIds(ids);
        }
    }, [reaServices, reaServicesLoadedForId, realEstateAsset.id, currentlyAssignedServiceIds, isEditing]);

    function getApiErrorMessage(err: unknown): string {
        // Checked before the generic 409 branch below, which would otherwise mask it.
        if (isProblemDetailCode(err, 'DUPLICATE_SERVICE_TYPE_FOR_ASSET')) {
            return t('Cannot add more than one of the same type of service to the same REA.');
        }
        if (err && typeof err === 'object' && 'status' in err) {
            const status = (err as { status: number }).status;
            if (status === 404) return t('Service or mapping not found.');
            if (status === 409) return t('A mapping already exists for this service.');
            if (status === 502 || status === 503) return t('Service temporarily unavailable. Please try again.');
        }
        return isProblemDetailTextOr(err, t('An unexpected error occurred.'));
    }

    const handleDropdownOpen = useCallback(() => {
        if (!isEditing) {
            setIsEditing(true);
            // Don't signal dirty yet — the user hasn't changed anything.
        }
    }, [isEditing]);

    const handleSelectionChange = useCallback(
        (ids: string[]) => {
            if (isSaving) return;
            setEditServiceIds(ids);
            const isDirty = ids.length !== initialEditIds.size || ids.some((id) => !initialEditIds.has(id));
            onEditingChange?.(isDirty);
        },
        [isSaving, initialEditIds, onEditingChange],
    );

    function handleCancelEdit() {
        setIsEditing(false);
        const ids = [...currentlyAssignedServiceIds];
        setInitialEditIds(new Set(ids));
        setEditServiceIds(ids);
        onEditingChange?.(false);
    }

    async function handleSaveEdit() {
        const editSet = new Set(editServiceIds);
        const toAdd = editServiceIds.filter((id) => !currentlyAssignedServiceIds.has(id));
        const toRemove = [...currentlyAssignedServiceIds].filter((id) => !editSet.has(id));

        if (toAdd.length === 0 && toRemove.length === 0) {
            setIsEditing(false);
            return;
        }

        // GEN-664: max one service of each type per REA — the type being the HubSpot
        // service_key, or the sku for services without one (GEN-830, same as the
        // backend). Only block when a service being ADDED participates in the
        // duplicate — pre-existing duplicate data must not prevent removals or
        // unrelated edits.
        const typeKeyCounts = new Map<string, number>();
        for (const id of editServiceIds) {
            const typeKey = serviceMetaById.get(id)?.typeKey;
            if (typeKey) typeKeyCounts.set(typeKey, (typeKeyCounts.get(typeKey) ?? 0) + 1);
        }
        const addsDuplicateType = toAdd.some((id) => {
            const typeKey = serviceMetaById.get(id)?.typeKey;
            return !!typeKey && (typeKeyCounts.get(typeKey) ?? 0) > 1;
        });
        if (addsDuplicateType) {
            dispatch(
                showSnackbar({
                    message: t('Cannot add more than one of the same type of service to the same REA.'),
                    severity: NotificationSeverity.Error,
                }),
            );
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                id: realEstateAsset.id,
                code: realEstateAsset.code,
                name: realEstateAsset.name,
                mainAddress: realEstateAsset.mainAddress,
            };

            // No bulk endpoint — fire individual POSTs/DELETEs in parallel.
            // allSettled so a single failure doesn't drop still-pending siblings.
            const addResults = await Promise.allSettled(
                toAdd.map((serviceId) =>
                    dispatch(createServiceAssetMapping({ serviceId, request: { realEstateAsset: payload } }))
                        .unwrap()
                        .catch((err) => {
                            throw { serviceId, op: 'add' as const, err };
                        }),
                ),
            );
            const removeResults = await Promise.allSettled(
                toRemove.map((serviceId) =>
                    dispatch(deleteServiceAssetMapping({ serviceId }))
                        .unwrap()
                        .catch((err) => {
                            throw { serviceId, op: 'remove' as const, err };
                        }),
                ),
            );

            const failures = [...addResults, ...removeResults].filter(
                (r): r is PromiseRejectedResult => r.status === 'rejected',
            );
            for (const f of failures) {
                const { serviceId, op, err } = f.reason as {
                    serviceId: string;
                    op: 'add' | 'remove';
                    err: unknown;
                };
                const prefix = op === 'add' ? t('Failed to assign service') : t('Failed to unassign service');
                dispatch(
                    showSnackbar({
                        message: `${prefix} ${serviceId.slice(0, 8)}: ${getApiErrorMessage(err)}`,
                        severity: NotificationSeverity.Error,
                    }),
                );
            }

            // Always reconcile UI with actual backend state, whether the batch fully succeeded or not.
            // Awaited so the spinner stays visible until the refreshed data has arrived.
            // .unwrap() surfaces thunk rejections; .catch() keeps a refresh failure from
            // blocking the success-path UI update that follows.
            await Promise.all([
                dispatch(fetchRealEstateAssetServices(realEstateAsset.id)).unwrap(),
                dispatch(fetchUnassignedServices()).unwrap(),
            ]).catch((err) => {
                console.warn('Post-save data refresh failed', err);
                dispatch(
                    showSnackbar({
                        message: t('Saved, but could not load the latest data — try reloading.'),
                        severity: NotificationSeverity.Warning,
                    }),
                );
            });
            // Advance the snapshot for every operation that succeeded, whether the
            // batch fully succeeded or only partially. This ensures changeSummary only
            // shows the genuinely still-unsaved (failed) changes, not the ones that
            // already landed on the backend.
            const successfullyAdded = toAdd.filter((_, i) => addResults[i].status === 'fulfilled');
            const successfullyRemoved = toRemove.filter((_, i) => removeResults[i].status === 'fulfilled');
            if (successfullyAdded.length > 0 || successfullyRemoved.length > 0) {
                setInitialEditIds((prev) => {
                    const next = new Set(prev);
                    for (const id of successfullyAdded) next.add(id);
                    for (const id of successfullyRemoved) next.delete(id);
                    return next;
                });
            }

            if (failures.length === 0) {
                setIsEditing(false);
                // Keep editServiceIds as-is — the sync effect will reconcile with the
                // actual backend state once the fetch completes, avoiding a flicker of
                // empty checkboxes while the ServiceSelector is still visible.
                onEditingChange?.(false);
            }
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <Stack spacing={2}>
            <ServiceSelector
                selectedIds={editServiceIds}
                onChange={handleSelectionChange}
                groups={selectorGroups}
                isLoading={isLoadingUnassignedServices || isLoadingReaServices || isSaving}
                onOpen={handleDropdownOpen}
            />
            {isEditing && changeSummary.length > 0 && <UnsavedChangeSummary summary={changeSummary} />}
            {isEditing && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSaveEdit}
                        disabled={isSaving}
                        startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
                    >
                        {isSaving ? t('Saving…') : t('Save')}
                    </Button>
                    <Button variant="outlined" size="small" onClick={handleCancelEdit} disabled={isSaving}>
                        {t('Cancel')}
                    </Button>
                </Box>
            )}
            {reaServicesLoadedForId === realEstateAsset.id && reaServices.flatMap((d) => d.services).length === 0 && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                    {t('No HubSpot service linked. Link a service to enable service delivery.')}
                </Alert>
            )}
            <ServicesViewSection
                title={t('Active services')}
                emptyText={t('No active services')}
                deals={groupedServices.ACTIVE}
            />
            <ServicesViewSection
                title={t('Delivered services')}
                emptyText={t('No delivered services')}
                deals={groupedServices.DELIVERED}
            />
            {groupedServices.CANCELLED.length > 0 && (
                <ServicesViewSection
                    title={t('Cancelled services')}
                    emptyText={t('No cancelled services')}
                    deals={groupedServices.CANCELLED}
                />
            )}
        </Stack>
    );
}

/**
 * The Services tab, split into two sub-tabs (GEN-591): **Connections** — the
 * HubSpot service↔REA mapping management that used to be the whole tab — and
 * **Information** — per-service-group data-collection forms (SERVICE-entity
 * requirement fields). Information is greyed out until a service whose product
 * carries such fields is connected. Both sub-tabs stay mounted (`hidden`
 * toggle) so in-progress input survives switching; their dirty flags are
 * OR-combined into the parent page's unsaved-changes guard.
 */
export function ServicesTab({
    realEstateAsset,
    dataCollectionGroups,
    buildingOptions,
    onEditingChange,
}: {
    realEstateAsset: RealEstateAsset;
    dataCollectionGroups: ProductRequirementGroup[];
    buildingOptions: RequirementFieldOption[];
    onEditingChange?: (editing: boolean) => void;
}) {
    const { t } = useTranslation();
    const { reaServices } = useAppSelector((state) => state.realEstateAssetManagement);

    const [subTab, setSubTab] = useState<'connections' | 'information'>('connections');
    const [connectionsEditing, setConnectionsEditing] = useState(false);
    const [informationDirty, setInformationDirty] = useState(false);

    // Fresh REA → back to Connections; the Information form remounts via key.
    // Adjust-state-during-render (not an effect) so no stale frame is shown.
    const [lastReaId, setLastReaId] = useState(realEstateAsset.id);
    if (lastReaId !== realEstateAsset.id) {
        setLastReaId(realEstateAsset.id);
        setSubTab('connections');
        setInformationDirty(false);
    }

    useEffect(() => {
        onEditingChange?.(connectionsEditing || informationDirty);
    }, [connectionsEditing, informationDirty, onEditingChange]);

    const informationEnabled = useMemo(() => {
        const serviceInfoGroups = serviceInformationGroups(dataCollectionGroups);
        return enabledServiceInfoGroupCodes(serviceInfoGroups, activeProductIds(reaServices)).size > 0;
    }, [dataCollectionGroups, reaServices]);

    return (
        <Stack spacing={2}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={subTab} onChange={(_, value) => setSubTab(value)} sx={{ minHeight: 40 }}>
                    <Tab value="connections" label={t('Connections')} sx={{ minHeight: 40 }} />
                    <Tab
                        value="information"
                        label={t('Information')}
                        disabled={!informationEnabled}
                        sx={{ minHeight: 40 }}
                    />
                </Tabs>
            </Box>
            <Box hidden={subTab !== 'connections'}>
                <ConnectionsSubTab realEstateAsset={realEstateAsset} onEditingChange={setConnectionsEditing} />
            </Box>
            <Box hidden={subTab !== 'information'}>
                <ServiceInformationTab
                    key={realEstateAsset.id}
                    realEstateAsset={realEstateAsset}
                    groups={dataCollectionGroups}
                    buildingOptions={buildingOptions}
                    onDirtyChange={setInformationDirty}
                />
            </Box>
        </Stack>
    );
}
